import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaSyncAlt,
  FaBuilding,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaSearch,
} from "react-icons/fa";
import { getClubs } from "../../../services/clubServices";
import { getDojos } from "../../../services/dojoServices";
import { getGroupsByDojo } from "../../../services/groupServices";
import useScrollToTop from "../../../hooks/useScollToTop";

import ClubsDojoStats from "./components/ClubsDojoStats";
import ClubsDojoFilters from "./components/ClubsDojoFilters";
import ClubCard from "./components/ClubCard";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ClubsDojoAdmin = () => {
  useScrollToTop();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartement, setSelectedDepartement] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [expandedClubIds, setExpandedClubIds] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [clubsData, dojosData] = await Promise.all([getClubs(), getDojos()]);

      const safeClubs = Array.isArray(clubsData) ? clubsData : [];
      const safeDojos = Array.isArray(dojosData) ? dojosData : [];

      const dojosWithGroups = await Promise.all(
        safeDojos.map(async (dojo) => {
          try {
            const groups = await getGroupsByDojo(dojo.id);
            return {
              ...dojo,
              groupsCount: Array.isArray(groups) ? groups.length : 0,
            };
          } catch (error) {
            console.error(`Erreur récupération groupes dojo ${dojo.id}`, error);
            return {
              ...dojo,
              groupsCount: 0,
            };
          }
        })
      );

      const clubsWithDojos = safeClubs.map((club) => {
        const clubDojos = dojosWithGroups.filter(
          (dojo) => String(dojo.club_id) === String(club.id)
        );

        const totalGroups = clubDojos.reduce(
          (sum, dojo) => sum + (dojo.groupsCount || 0),
          0
        );

        return {
          ...club,
          dojos: clubDojos,
          totalGroups,
        };
      });

      setClubs(clubsWithDojos);
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const departementOptions = useMemo(() => {
    return [...new Set(clubs.map((club) => club.departement_club).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), "fr"));
  }, [clubs]);

  const stats = useMemo(() => {
    const totalClubs = clubs.length;
    const totalDojos = clubs.reduce(
      (sum, club) => sum + (club.dojos?.length || 0),
      0
    );
    const totalGroups = clubs.reduce(
      (sum, club) => sum + (club.totalGroups || 0),
      0
    );
    const clubsSansDojo = clubs.filter((club) => !club.dojos?.length).length;

    return {
      totalClubs,
      totalDojos,
      totalGroups,
      clubsSansDojo,
    };
  }, [clubs]);

  const filteredClubs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const result = clubs.filter((club) => {
      const userLabel = club.user
        ? `${club.user.nom || ""} ${club.user.prenom || ""}`.toLowerCase()
        : "";

      const dojoText = (club.dojos || [])
        .map((dojo) => `${dojo.nom_dojo || ""} ${dojo.adresse_dojo || ""}`)
        .join(" ")
        .toLowerCase();

      const clubText = `
        ${club.nom_club || ""}
        ${club.departement_club || ""}
        ${club.adresse_club || ""}
        ${userLabel}
        ${dojoText}
      `.toLowerCase();

      const matchesSearch = !term || clubText.includes(term);
      const matchesDepartement = selectedDepartement
        ? String(club.departement_club) === String(selectedDepartement)
        : true;

      return matchesSearch && matchesDepartement;
    });

    result.sort((a, b) => {
      switch (sortBy) {
        case "dojos_desc":
          return (b.dojos?.length || 0) - (a.dojos?.length || 0);

        case "groups_desc":
          return (b.totalGroups || 0) - (a.totalGroups || 0);

        case "departement":
          return String(a.departement_club || "").localeCompare(
            String(b.departement_club || ""),
            "fr"
          );

        case "name":
        default:
          return String(a.nom_club || "").localeCompare(
            String(b.nom_club || ""),
            "fr"
          );
      }
    });

    return result;
  }, [clubs, searchTerm, selectedDepartement, sortBy]);

  const toggleClub = (clubId) => {
    setExpandedClubIds((prev) =>
      prev.includes(clubId)
        ? prev.filter((id) => id !== clubId)
        : [...prev, clubId]
    );
  };

  const toggleExpandAll = () => {
    const visibleIds = filteredClubs.map((club) => club.id);
    const allExpanded =
      visibleIds.length > 0 &&
      visibleIds.every((id) => expandedClubIds.includes(id));

    setExpandedClubIds(allExpanded ? [] : visibleIds);
  };

  return (
    <div className="p-4 mt-[100px] w-full mx-auto font-satoshi">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="rounded-[28px] overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-500 px-6 py-7 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Gestion des Clubs & Dojos
                </h1>
                <p className="mt-2 text-white/90 text-sm md:text-base max-w-2xl">
                  Vue d’ensemble des clubs, des lieux d’entraînement et du nombre
                  de groupes associés.
                </p>
              </div>

              <button
                onClick={fetchData}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/15 hover:bg-white/25 px-4 py-2.5 text-white transition"
              >
                <FaSyncAlt className={cn(loading && "animate-spin")} />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6">
            <ClubsDojoStats stats={stats} />
          </div>
        </div>

        {/* Filtres */}
        <div className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 md:p-6">
          <ClubsDojoFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedDepartement={selectedDepartement}
            setSelectedDepartement={setSelectedDepartement}
            sortBy={sortBy}
            setSortBy={setSortBy}
            departementOptions={departementOptions}
            totalResults={filteredClubs.length}
            onToggleExpandAll={toggleExpandAll}
            allExpanded={
              filteredClubs.length > 0 &&
              filteredClubs.every((club) => expandedClubIds.includes(club.id))
            }
          />
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-52 rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm animate-pulse"
              />
            ))}
          </div>
        ) : filteredClubs.length === 0 ? (
          <div className="rounded-[28px] border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
              <FaSearch className="text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Aucun club trouvé
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              Essaie de modifier la recherche ou le filtre département.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {filteredClubs.map((club) => (
              <ClubCard
                key={club.id}
                club={club}
                expanded={expandedClubIds.includes(club.id)}
                onToggle={() => toggleClub(club.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubsDojoAdmin;